import { Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useContext, useMemo, useRef, useState } from 'react';
import { useSearchParamsUtils } from 'app/hooks/useSearchParamsUtils';
import map from 'lodash-es/map';
import { useNavigate } from 'react-router-dom';
import ListSkeleton from 'shared/lib/components/plugs/listSkeleton';
import EditFolderModalController, {
  EditFolderAfterReactions,
} from 'widgets/folders/editFolderModalController';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import { useBooleanScreens } from 'shared/hooks/useBooleanScreens';
import {
  ContentFileItem,
  ContentFolderItem,
  ContentItem,
  ContentTableItem,
  ExplorerEntityTypes,
  SortContentKey,
  SortContentOrder,
} from 'entities/types';
import ContentGridContainer from 'shared/lib/extendedComponents/grids/content/contentGridContainer';
import ContentFolderRow from 'shared/lib/extendedComponents/grids/content/contentFolderRow';
import ContentFileRow from 'shared/lib/extendedComponents/grids/content/contentFileRow';
import { downloadFile } from 'app/api/api';
import mobxStore from 'app/store/rootStore';
import { useTranslation } from 'react-i18next';
import { filesApi } from 'entities/file/api/filesApi';
import ContentTableRow from 'shared/lib/extendedComponents/grids/content/contentTableRow';
import { tablesApi } from 'entities/table/api/tablesApi';
import EditFileModalController, {
  EditFileModalActions,
} from 'widgets/files/editFileModalController';
import EditTableModalController, {
  EditTableAfterReactions,
} from 'widgets/tables/editTableModalController';
import DesktopContentHeaderRow from 'shared/lib/extendedComponents/grids/content/contentHeaderRow/desktopContentHeaderRow';
import MobileContentHeaderRow from 'shared/lib/extendedComponents/grids/content/contentHeaderRow/mobileContentHeaderRow';

export default observer(function ContentGridController({
  items,
  sort,
  onSortChange,
  navigatePathname,
  supportTypes = [
    ExplorerEntityTypes.FILE,
    ExplorerEntityTypes.FOLDER,
    ExplorerEntityTypes.TABLE,
  ],
  afterFolderDelete,
  afterFolderMerge,
  afterFolderUpdate,
  afterFileDelete,
  afterFileUpdate,
  afterTableDelete,
  afterTableUpdate,
  afterFolderCreate,
  isFavorites,
}: {
  afterFolderCreate?: () => void;
  items: ContentItem[] | null;
  navigatePathname?: string;
  isFavorites?: boolean;
  onSortChange?: (params: {
    key: SortContentKey;
    order: SortContentOrder;
  }) => void;
  sort?: { key: SortContentKey; order: SortContentOrder };
  supportTypes?: ExplorerEntityTypes[];
} & EditFolderAfterReactions &
  EditFileModalActions &
  EditTableAfterReactions) {
  const navigate = useNavigate();
  const {
    mobxStore: {
      authStore: { user },
    },
  } = useContext(MobxContext);
  const { t } = useTranslation();

  const [editingFile, setEditingFile] = useState<ContentFileItem | null>(null);
  const [editingTable, setEditingTable] = useState<ContentTableItem | null>(
    null
  );
  const [editingFolder, setEditingFolder] = useState<ContentFolderItem | null>(
    null
  );

  const { getFolderSearchParams, getFileSearchParams, getTableSearchParams } =
    useSearchParamsUtils();

  const navigateToFile = useRef(({ fileId }: { fileId: string }) => {
    navigate({
      pathname: navigatePathname,
      search: getFileSearchParams({
        fileId,
      }),
    });
  });

  const navigateToTable = useRef(({ tableId }: { tableId: string }) => {
    navigate({
      pathname: navigatePathname,
      search: getTableSearchParams({
        tableId,
      }),
    });
  });

  const navigateToFolder = useRef(({ folderId }: { folderId: string }) => {
    navigate({
      pathname: navigatePathname,
      search: getFolderSearchParams({
        folderId,
      }),
    });
  });

  const { isLessLaptop } = useBooleanScreens();

  const _onSortChange = useRef(onSortChange);
  _onSortChange.current = onSortChange;
  const _supportTypes = useRef(supportTypes);
  _supportTypes.current = supportTypes;

  const { key, order } = sort || {};
  const memoSort = useMemo(() => {
    if (key && order) {
      return {
        key,
        order,
      };
    }

    return;
  }, [key, order]);

  const grid = useMemo(() => {
    return (
      <ContentGridContainer supportTypes={_supportTypes.current}>
        {isLessLaptop ? (
          <MobileContentHeaderRow
            value={memoSort}
            onChange={_onSortChange.current}
            supportTypes={_supportTypes.current}
          ></MobileContentHeaderRow>
        ) : (
          <DesktopContentHeaderRow
            supportTypes={_supportTypes.current}
            value={memoSort}
            onChange={_onSortChange.current}
          ></DesktopContentHeaderRow>
        )}
        {map(items, (item) => {
          if (item.type === ExplorerEntityTypes.FOLDER) {
            return (
              <ContentFolderRow
                key={item.id}
                data={item}
                supportTypes={_supportTypes.current}
                onClick={() => {
                  navigateToFolder.current({
                    folderId: item.id,
                  });
                }}
                isSettingsDisabled={user?.isOnlyView}
                onEdit={() => {
                  setEditingFolder(item);
                }}
              ></ContentFolderRow>
            );
          }

          if (item.type === ExplorerEntityTypes.FILE) {
            return (
              <ContentFileRow
                key={item.id}
                data={item}
                supportTypes={_supportTypes.current}
                onClick={() => {
                  navigateToFile.current({
                    fileId: item.id,
                  });
                }}
                onFavorite={async () => {
                  try {
                    await filesApi.setFavourite(item.id, !item.is_favorite);
                    item.is_favorite = !item.is_favorite;
                    if (isFavorites && !item.is_favorite) {
                      afterFileDelete && afterFileDelete(item.id);
                    }
                  } catch (e) {
                    mobxStore.notificationsStore.errorMessage = t(
                      'notifications.errors.default'
                    );
                    console.error(e);
                  }
                }}
                onEdit={
                  user?.isOnlyView
                    ? undefined
                    : () => {
                        setEditingFile(item);
                      }
                }
                onDownload={() =>
                  downloadFile({
                    id: item.id,
                    name: item.name || '',
                  })
                }
              ></ContentFileRow>
            );
          }
          if (item.type === ExplorerEntityTypes.TABLE) {
            return (
              <ContentTableRow
                key={item.id}
                data={item}
                supportTypes={_supportTypes.current}
                onClick={() => {
                  navigateToTable.current({
                    tableId: item.id,
                  });
                }}
                onFavorite={async () => {
                  try {
                    await tablesApi.setFavourite(item.id, !item.is_favorite);
                    item.is_favorite = !item.is_favorite;
                    if (isFavorites && !item.is_favorite) {
                      afterTableDelete && afterTableDelete(item.id);
                    }
                  } catch (e) {
                    mobxStore.notificationsStore.errorMessage = t(
                      'notifications.errors.default'
                    );
                    console.error(e);
                  }
                }}
                onEdit={
                  user?.isOnlyView
                    ? undefined
                    : () => {
                        setEditingTable(item);
                      }
                }
                onDownload={() => tablesApi.download(item.id, item.name || '')}
              ></ContentTableRow>
            );
          }
        })}
      </ContentGridContainer>
    );
  }, [isLessLaptop, items, memoSort, user?.isOnlyView, t]);

  return (
    <>
      {items !== null ? (
        items.length ? (
          grid
        ) : (
          <Typography sx={{ fontWeight: 'bold' }} variant="f20">
            Здесь ничего нет
          </Typography>
        )
      ) : (
        <ListSkeleton></ListSkeleton>
      )}

      {editingFolder && (
        <EditFolderModalController
          updatable={editingFolder}
          handleClose={() => setEditingFolder(null)}
          afterFolderDelete={afterFolderDelete}
          afterFolderUpdate={afterFolderUpdate}
          afterFolderMerge={afterFolderMerge}
          afterFolderCreate={afterFolderCreate}
        ></EditFolderModalController>
      )}

      {editingFile && (
        <EditFileModalController
          updatable={editingFile}
          handleClose={() => setEditingFile(null)}
          afterFileDelete={afterFileDelete}
          afterFileUpdate={afterFileUpdate}
        ></EditFileModalController>
      )}

      {editingTable && (
        <EditTableModalController
          updatable={editingTable}
          handleClose={() => setEditingTable(null)}
          afterTableDelete={afterTableDelete}
          afterTableUpdate={afterTableUpdate}
        ></EditTableModalController>
      )}
    </>
  );
});
